(function () {
  // Constants
  const CONSTANTS = {
    API_VERSION: '2024-07',
    API_ENDPOINT: '/api/2024-07/graphql.json',
    METAFIELD_NAMESPACE: 'esim',
    METAFIELD_KEY_DAYS: 'days',
    METAFIELD_KEY_FROM_PRICE: 'from_price',
    METAFIELD_NAMESPACE_CUSTOM: 'custom',
    DYNAMIC_ESIM_PRODUCT_TYPE: 'Dynamic eSIMs',
    METAFIELD_KEY_CARD_SPECIFICATION: 'card_specification',
    UNLIMITED_VALUE: 'Unlimited',
    DEFAULT_DAYS: 7,
    // The `esim.from_price` metafield value is stored in shop base currency (AUD).
    FROM_PRICE_BASE_CURRENCY: 'AUD',
    CACHE_EXPIRY: 5 * 60 * 1000, // 5 minutes
    BATCH_SIZE: 5,
    MOBILE_BREAKPOINT: 768,
    OVERLAY_CLICK_DELAY: 50,
    TRANSITION_DURATION: 300,
    FOCUS_DELAY: 100,
  };

  class DestinationsSearchModal extends HTMLElement {
    connectedCallback() {
      // Modal elements
      this.modalOverlay = this.querySelector('.ds-modal-overlay');
      this.modalDialog = this.querySelector('.ds-modal-dialog');
      this.modalInput = this.querySelector('.dsearch__input--modal');
      this.modalDropdown = this.querySelector('.dsearch__dropdown--modal');
      this.triggerInput = this.querySelector('.dsearch__input:not(.dsearch__input--modal)');

      this.countries = [];
      this.regions = [];
      this.isLoading = false;
      this.isDataLoaded = false;
      this.cacheExpiry = CONSTANTS.CACHE_EXPIRY;
      this.lastFetch = 0;
      this.debounceTimer = null; // Used only for event logging, not search
      this.isModalOpen = false;
      this.lockedAppHeight = null; // Store locked height for mobile
      this.bodyScrollY = 0;
      this.touchMoveHandler = null;

      if (!this.modalOverlay || !this.modalDialog || !this.modalInput) {
        console.warn('DestinationsSearchModal: Required elements not found');
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.escKeyHandler) {
        document.removeEventListener('keydown', this.escKeyHandler);
      }
      this.removeOverlayClickHandler();
    }

    removeOverlayClickHandler() {
      if (this.overlayClickHandler && this.modalOverlay) {
        this.modalOverlay.removeEventListener('click', this.overlayClickHandler);
        this.overlayClickHandler = null;
      }
    }

    addOverlayClickHandler() {
      // Only add if not already added
      if (this.overlayClickHandler || !this.modalOverlay) {
        return;
      }

      this.overlayClickHandler = (e) => {
        // Only process if modal is open
        if (!this.isModalOpen) {
          return;
        }

        // Don't close if clicking inside modal dialog
        if (this.modalDialog && this.modalDialog.contains(e.target)) {
          // Handle link clicks
          const link = e.target.closest('a');
          if (link) {
            e.preventDefault();
            this.sendSearchEventLog(link.innerText, true);
            this.closeModal();
            window.location.href = link.href;
          }
          return;
        }

        // Don't close if clicking on close button
        const closeButton = this.querySelector('.ds-modal-close');
        if (closeButton && closeButton.contains(e.target)) {
          this.closeModal();
          return;
        }

        // Click is on overlay (not dialog) - close modal
        // Only close if the click target is actually the overlay itself
        if (e.target === this.modalOverlay) {
          this.closeModal();
        }
      };

      // Attach handler with a small delay to avoid catching the trigger click
      setTimeout(() => {
        if (this.modalOverlay && this.overlayClickHandler) {
          this.modalOverlay.addEventListener('click', this.overlayClickHandler);
        }
      }, CONSTANTS.OVERLAY_CLICK_DELAY);
    }

    init() {
      // Initialize trigger input (main input that opens modal)
      if (this.triggerInput) {
        this.triggerInput.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent event from reaching overlay
          if (!this.isModalOpen) {
            this.openModal();
          }
        });

        this.triggerInput.addEventListener('focus', () => {
          if (!this.isModalOpen) {
            this.openModal();
          }
        });
      }

      // Modal input event listeners
      this.modalInput.addEventListener('input', this.onInput.bind(this));
      this.modalInput.addEventListener('focus', this.onFocus.bind(this));
      this.modalInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      // Close button event listeners (both desktop and mobile)
      const closeButtons = this.querySelectorAll('.ds-modal-close');
      closeButtons.forEach((closeButton) => {
        closeButton.addEventListener('click', () => {
          this.closeModal();
        });
      });

      // ESC key handler for document
      this.escKeyHandler = (e) => {
        if (e.key === 'Escape' && this.isModalOpen) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this.escKeyHandler);

      this.storefrontToken =
        this.getAttribute('data-token') || window?.themeSettings?.storefrontToken;

      // Global eSIM: when a searched country is in this product's countries_covered, show Global in results
      this.globalEsimCountries = new Set();
      this.globalEsimUrl = '';
      this.globalEsimProductGid = '';
      this.globalEsimHandle = '';
      this.globalEsimTitle = 'Global';
      this.globalEsimIconUrl = '';
      this.globalEsimNew = false;
      const countriesAttr = this.getAttribute('data-global-esim-countries');
      if (countriesAttr && countriesAttr.trim()) {
        this.globalEsimUrl = this.getAttribute('data-global-esim-url') || '';
        this.globalEsimProductGid = this.getAttribute('data-global-esim-product-gid') || '';
        this.globalEsimHandle = this.getAttribute('data-global-esim-handle') || '';
        const titleAttr = this.getAttribute('data-global-esim-title');
        if (titleAttr && titleAttr.trim()) this.globalEsimTitle = titleAttr.trim();
        const iconAttr = this.getAttribute('data-global-esim-icon');
        if (iconAttr && iconAttr.trim()) this.globalEsimIconUrl = iconAttr.trim();
        this.globalEsimNew = this.getAttribute('data-global-esim-new') === 'true';
        countriesAttr
          .split('|')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean)
          .forEach((c) => this.globalEsimCountries.add(c));
      }

      // Preload data silently in the background
      this.preloadDataSilently();
    }

    openModal() {
      if (!this.modalOverlay || !this.modalDialog) return;

      // Prevent opening if already open
      if (this.isModalOpen) {
        return;
      }

      // Blur trigger input to prevent keyboard from opening on mobile
      if (this.triggerInput && document.activeElement === this.triggerInput) {
        this.triggerInput.blur();
      }

      this.isModalOpen = true;

      // Ensure dialog is inside overlay
      if (this.modalDialog.parentNode !== this.modalOverlay) {
        this.modalOverlay.appendChild(this.modalDialog);
      }

      // Move overlay to body for proper positioning
      if (this.modalOverlay.parentNode !== document.body) {
        document.body.appendChild(this.modalOverlay);
      }

      // Remove transition-complete class and hidden attribute
      this.modalOverlay.classList.remove('transition-complete');
      this.modalOverlay.removeAttribute('hidden');
      this.modalDialog.removeAttribute('hidden');

      // Check if mobile
      const isMobile = window.innerWidth < CONSTANTS.MOBILE_BREAKPOINT;

      // On mobile, lock the height to prevent adjustment when keyboard opens
      if (isMobile) {
        try {
          // Capture window.innerHeight before keyboard opens (this is the full viewport height)
          // This will be locked and won't change even when keyboard opens
          const currentAppHeight = window.innerHeight + 'px';

          // Store it for cleanup
          this.lockedAppHeight = currentAppHeight;

          // Set a custom CSS variable with the locked height
          document.documentElement.style.setProperty('--modal-locked-height', currentAppHeight);

          // Add class to apply locked height via CSS
          this.modalOverlay.classList.add('height-locked');
          this.modalDialog.classList.add('height-locked');
        } catch (e) {
          // If height locking fails, continue anyway - modal should still open
          console.warn('Failed to lock modal height:', e);
        }
      }

      // Force reflow to ensure transitions work
      void this.modalOverlay.offsetWidth;

      // Lock body scroll on mobile to prevent page scroll under modal
      if (isMobile) {
        this.lockBodyScroll();
        this.bindTouchMoveLock();
      } else {
        document.body.style.overflow = 'hidden';
      }

      // Add overlay click handler when modal opens
      this.addOverlayClickHandler();

      // Focus modal input only on desktop (not mobile to avoid keyboard opening)
      if (this.modalInput && !isMobile) {
        setTimeout(() => {
          // Double check we're still on desktop before focusing
          if (window.innerWidth >= CONSTANTS.MOBILE_BREAKPOINT) {
            this.modalInput.focus();
          }
        }, CONSTANTS.FOCUS_DELAY);
      }

      // Show popular destinations when modal opens (if no search query)
      if (this.modalInput && !this.modalInput.value.trim()) {
        setTimeout(() => {
          this.showPopularDestinations();
        }, CONSTANTS.FOCUS_DELAY);
      }
    }

    closeModal() {
      if (!this.modalOverlay || !this.modalDialog) return;

      // Prevent closing if already closed
      if (!this.isModalOpen) {
        return;
      }

      this.isModalOpen = false;

      // Remove overlay click handler when modal closes
      this.removeOverlayClickHandler();

      this.modalOverlay.classList.remove('transition-complete');

      // Trigger closing transition
      this.modalOverlay.setAttribute('hidden', '');
      this.modalDialog.setAttribute('hidden', '');

      // Wait for transition to complete
      setTimeout(() => {
        if (window.innerWidth < CONSTANTS.MOBILE_BREAKPOINT) {
          this.unlockBodyScroll();
          this.unbindTouchMoveLock();
        } else {
          document.body.style.overflow = '';
        }
        this.modalOverlay.classList.add('transition-complete');

        // Clean up locked height on mobile
        if (this.lockedAppHeight) {
          this.modalOverlay.classList.remove('height-locked');
          this.modalDialog.classList.remove('height-locked');
          document.documentElement.style.removeProperty('--modal-locked-height');
          this.lockedAppHeight = null;
        }

        // Move modal elements back to original location
        if (this.modalOverlay.parentNode === document.body) {
          this.appendChild(this.modalOverlay);
        }
      }, CONSTANTS.TRANSITION_DURATION);
    }

    lockBodyScroll() {
      this.bodyScrollY = window.scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.bodyScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (this.bodyScrollY) {
        window.scrollTo(0, this.bodyScrollY);
      }
      this.bodyScrollY = 0;
    }

    bindTouchMoveLock() {
      if (this.touchMoveHandler || !this.modalOverlay) {
        return;
      }
      this.touchMoveHandler = (event) => {
        const dropdown = event.target.closest('.dsearch__dropdown--modal');
        if (!dropdown) {
          event.preventDefault();
        }
      };
      this.modalOverlay.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    }

    unbindTouchMoveLock() {
      if (this.touchMoveHandler && this.modalOverlay) {
        this.modalOverlay.removeEventListener('touchmove', this.touchMoveHandler);
      }
      this.touchMoveHandler = null;
    }

    onFocus() {
      // Show popular destinations when modal input is focused (if no search query)
      const q = this.modalInput.value.trim();
      if (!q) {
        this.showPopularDestinations();
      }
    }

    async onInput(e) {
      // Get current input value
      const currentValue = this.modalInput.value.trim();
      const q = currentValue.toLowerCase();

      // If input is empty, show popular destinations
      if (!q) {
        this.showPopularDestinations();
        return;
      }

      if (!this.storefrontToken) {
        return;
      }

      // If data is already loaded, search immediately
      if (this.isDataLoaded) {
        await this.searchLocal(q);
        return;
      }

      // If data is loading, show loading state
      if (this.isLoading) {
        this.showLoading();
        return;
      }

      // If data needs refresh, reload it
      if (this.needsRefresh()) {
        await this.preloadData();
        await this.searchLocal(q);
        return;
      }

      // Otherwise, preload data and search
      await this.searchLive(q);
    }

    async searchLive(q) {
      await this.preloadData();
      await this.searchLocal(q);
    }

    // Helper: Execute GraphQL query
    async executeGraphQLQuery(query, variables = {}) {
      try {
        const response = await fetch(CONSTANTS.API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        return data.data;
      } catch (error) {
        console.error('GraphQL query error:', error);
        throw error;
      }
    }

    // Country code for Storefront API @inContext so Shopify returns prices in buyer's market (Shopify conversion).
    getContextCountry() {
      const raw = typeof window.Shopify !== 'undefined' && window.Shopify.country;
      if (!raw || typeof raw !== 'string') return null;
      const code = raw.trim().toUpperCase().slice(0, 2);
      return code.length === 2 ? code : null;
    }

    // Display currency: cart first (set by theme), then currency selector, else AUD.
    getDisplayCurrency() {
      const parseCode = (t) => (t && typeof t === 'string' && (t = t.trim().match(/\b([A-Z]{3})\b/)) ? t[1] : null);
      const fromCart = parseCode(window.cartCurrency);
      if (fromCart) return fromCart;
      const el = document.querySelector('currency-selector .cs-currency.is-active, currency-selector .cs-currency[aria-current="true"], currency-selector .cs-trigger span');
      return (el && parseCode(el.textContent)) || 'AUD';
    }

    // Convert amount to display currency. When API returns display currency (@inContext) we use it; else convert via theme exchange table (no rounding – per-day display).
    convertToDisplayCurrency(amount, fromCurrencyCode) {
      const displayCurrency = this.getDisplayCurrency();
      if (fromCurrencyCode === displayCurrency || !amount || typeof amount !== 'number') return amount;
      const table = window.exchangeTable;
      if (!table || typeof table !== 'object') return amount;
      const rateFrom = table[fromCurrencyCode];
      const rateToCart = table[displayCurrency];
      if (!rateFrom || !rateToCart) return amount;
      return (amount / rateFrom) * rateToCart;
    }

    // Helper: Find variant with minimum per-day price from product/collection variants
    findMinPriceVariant(variants) {
      if (!variants || !variants.edges || !variants.edges.length) {
        return null;
      }

      let minPerDayPrice = null;
      let minPriceVariant = null;

      variants.edges.forEach((variantEdge) => {
        const variant = variantEdge.node;
        if (variant.price) {
          const priceAmount = parseFloat(variant.price.amount);
          const daysValue = variant.metafield?.value;
          const days =
            daysValue && daysValue.trim() !== '' ? parseFloat(daysValue) : CONSTANTS.DEFAULT_DAYS;

          const perDayPrice = priceAmount / days;
          const perDayInCartCurrency = this.convertToDisplayCurrency(
            perDayPrice,
            variant.price.currencyCode
          );

          // Find the variant with the minimum per-day price (best value) in cart currency
          if (minPerDayPrice === null || perDayInCartCurrency < minPerDayPrice) {
            minPerDayPrice = perDayInCartCurrency;
            minPriceVariant = variant;
          }
        }
      });

      return minPriceVariant;
    }

    // Helper: Calculate per-day price from variant
    calculatePerDayPrice(variant) {
      if (!variant || !variant.price) {
        return null;
      }

      const priceAmount = parseFloat(variant.price.amount);
      const daysValue = variant.metafield?.value;
      const days =
        daysValue && daysValue.trim() !== '' ? parseFloat(daysValue) : CONSTANTS.DEFAULT_DAYS;

      return {
        amount: priceAmount / days,
        currencyCode: variant.price.currencyCode,
      };
    }

    // Price for display: per-day from variant (same shape as calculatePerDayPrice)
    getPriceForDisplay(variant) {
      return this.calculatePerDayPrice(variant);
    }

    // Use esim.from_price metafield when product type is Dynamic eSIMs (conversion via exchangeTable in formatPrice)
    getPriceFromFromPriceMetafield(product) {
      if (!product || product.productType !== CONSTANTS.DYNAMIC_ESIM_PRODUCT_TYPE) return null;
      const value = product.from_price_metafield?.value ?? product.metafield_from_price?.value;
      if (value == null || value === '') return null;
      const amount = parseFloat(value);
      if (Number.isNaN(amount)) return null;
      // NOTE: `@inContext` localizes variant prices, but `from_price` is not localized.
      // Treat the metafield number as being in shop base currency so conversion is correct.
      return { amount, currencyCode: CONSTANTS.FROM_PRICE_BASE_CURRENCY, isFromPriceMetafield: true };
    }

    // Per-day amount in cart currency (for min-price comparison across products)
    getPerDayAmountInCartCurrency(variant) {
      const perDay = this.calculatePerDayPrice(variant);
      if (!perDay) return null;
      return this.convertToDisplayCurrency(perDay.amount, perDay.currencyCode);
    }

    // Helper: Format price for display (per-day amount, converted to cart currency)
    formatPrice(price, currency) {
      if (!price || price.amount == null) return '';

      const amount = typeof price.amount === 'number' ? price.amount : parseFloat(price.amount);
      const fromCurrency = price.currencyCode || this.getDisplayCurrency();
      const displayCurrency = this.getDisplayCurrency();
      const displayAmount =
        fromCurrency === displayCurrency ? amount : this.convertToDisplayCurrency(amount, fromCurrency);

      // Modal shows per-day price.
      // - Dynamic eSIM `from_price`: convert via exchangeTable (already done above), then just round to 2 decimals.
      // - Non-Dynamic/variant-based: truncate to 2 decimals to match PDP.
      const truncatedAmount = price.isFromPriceMetafield
        ? Math.round(displayAmount * 100) / 100
        : Math.floor(displayAmount * 100 + 1e-6) / 100;

      const formattedPrice = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: displayCurrency,
        currencyDisplay: 'narrowSymbol',
      }).format(truncatedAmount).replace(/\.00$/, '');

      return `From ${formattedPrice}/day`;
    }

    resolveLocalizedUrl(rawUrl) {
      if (!rawUrl) return rawUrl;

      // Parse URL safely (handles absolute + relative)
      let url;
      try {
        url = rawUrl.startsWith('http') ? new URL(rawUrl) : new URL(rawUrl, window.location.origin);
      } catch (e) {
        return rawUrl;
      }

      const path = url.pathname;

      // Detect current market prefix from location
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const firstSegment = pathParts[0];

      const isMarketPrefix = /^[a-z]{2}-[a-z]{2}$/i.test(firstSegment);
      const marketPrefix = isMarketPrefix ? `/${firstSegment.toLowerCase()}` : '';

      // Strip any existing market prefix from the incoming URL
      const cleanedPath = path.replace(/^\/[a-z]{2}-[a-z]{2}(?=\/)/i, '');

      // Only localize Shopify resource URLs
      if (!cleanedPath.startsWith('/products') && !cleanedPath.startsWith('/collections')) {
        return rawUrl;
      }

      return `${marketPrefix}${cleanedPath}${url.search}${url.hash}`;
    }

    extractCollectionHandleFromUrl(url) {
      if (!url) return null;
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const collectionsIndex = pathParts.indexOf('collections');
        if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
          return pathParts[collectionsIndex + 1];
        }
      } catch (e) {
        // Invalid URL, return null
      }
      return null;
    }

    extractProductHandleFromUrl(url) {
      if (!url) return null;
      try {
        const urlObj = new URL(url, window.location.origin || 'https://localhost');
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        const productsIndex = pathParts.indexOf('products');
        if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
          return pathParts[productsIndex + 1];
        }
      } catch (e) {
        // Invalid URL, return null
      }
      return null;
    }

    async fetchCollectionPriceByHandle(collectionHandle) {
      const contextDir = this.getContextCountry() ? ` @inContext(country: ${this.getContextCountry()})` : '';
      const query = `query FetchCollectionByHandle($handle: String!)${contextDir} {
        collection(handle: $handle) {
          id
          products(first: 10) {
            edges {
              node {
                productType
                metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE_CUSTOM}", key: "${CONSTANTS.METAFIELD_KEY_CARD_SPECIFICATION}") {
                  value
                }
                from_price_metafield: metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_FROM_PRICE}") {
                  value
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_DAYS}") {
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`;

      try {
        const data = await this.executeGraphQLQuery(query, { handle: collectionHandle });
        const collection = data?.collection;
        if (!collection || !collection.products?.edges?.length) {
          return null;
        }

        let minPriceResult = null;
        let minCandidateInCart = null;
        let hasUnlimited = false;

        const considerProduct = (product) => {
          const cardSpec = product.metafield?.value || '';
          const isUnlimited = cardSpec.includes(CONSTANTS.UNLIMITED_VALUE);
          let candidateInCart = null;
          let candidatePrice = null;

          const fromPrice = this.getPriceFromFromPriceMetafield(product);
          if (fromPrice) {
            candidateInCart = this.convertToDisplayCurrency(fromPrice.amount, fromPrice.currencyCode);
            candidatePrice = fromPrice;
          } else if (product.variants) {
            const variant = this.findMinPriceVariant(product.variants);
            if (variant) {
              candidateInCart = this.getPerDayAmountInCartCurrency(variant);
              candidatePrice = this.getPriceForDisplay(variant);
            }
          }

          if (candidateInCart != null && candidatePrice != null) {
            if (minCandidateInCart == null || candidateInCart < minCandidateInCart) {
              minCandidateInCart = candidateInCart;
              minPriceResult = candidatePrice;
            }
          }
        };

        collection.products.edges.forEach((edge) => {
          const product = edge.node;
          const cardSpec = product.metafield?.value || '';
          const isUnlimited = cardSpec.includes(CONSTANTS.UNLIMITED_VALUE);
          if (isUnlimited) hasUnlimited = true;
        });

        if (hasUnlimited) {
          collection.products.edges.forEach((edge) => {
            const product = edge.node;
            const cardSpec = product.metafield?.value || '';
            if (cardSpec.includes(CONSTANTS.UNLIMITED_VALUE)) considerProduct(product);
          });
        }
        if (!hasUnlimited || minPriceResult == null) {
          collection.products.edges.forEach((edge) => {
            const product = edge.node;
            const cardSpec = product.metafield?.value || '';
            if (!cardSpec.includes(CONSTANTS.UNLIMITED_VALUE)) considerProduct(product);
          });
        }

        return minPriceResult;
      } catch (error) {
        console.error('Error fetching collection price by handle:', error);
        return null;
      }
    }

    render(countries, regions = []) {
      if (!this.modalDropdown) return;

      if (!countries.length && !regions.length) {
        this.modalDropdown.innerHTML = '<div class="dsearch__group-title">No matches</div>';
        this.modalDropdown.hidden = false;
        return;
      }

      // Render immediately without prices for better performance
      let html = '';

      // Render countries
      if (countries.length > 0) {
        const countryListHtml = countries
          .map((country, index) => {
            const icon = country.icon_url
              ? `<img src="${country.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
              : '';

            // Add data attribute for price fetching and row identity (so we only update the correct row)
            const itemId = `country-${index}`;
            const url = this.resolveLocalizedUrl(country.url);
            const titleAttr = (country.title || '').replace(/"/g, '&quot;');
            return `<li class="dsearch__item" data-item-id="${itemId}" data-item-title="${titleAttr}" data-url="${url}"><a class="dsearch__link" href="${url}"><span class="dsearch__result-content">${icon}<span class="dsearch__link-content"><span class="dsearch__result-name">${country.title}</span><span class="dsearch__price" data-price-target="${itemId}"><span class="dsearch__price-skeleton"></span></span></span></span><span class="dsearch__group-title">Country</span></a></li>`;
          })
          .join('');
        html += `<ul class="dsearch__list">${countryListHtml}</ul>`;
      }

      // Render regions
      if (regions.length > 0) {
        const regionListHtml = regions
          .map((region, index) => {
            const icon = region.icon_url
              ? `<img src="${region.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;" onerror="this.style.display='none'">`
              : '';

            const itemId = `region-${index}`;
            const url = this.resolveLocalizedUrl(region.url);
            const titleAttr = (region.title || '').replace(/"/g, '&quot;');
            const linkClass =
              region.isGlobalProduct ? 'dsearch__link' : `dsearch__link${region.title === 'Global' ? ' new-search__item' : ''}`;

            if (region.isGlobalProduct) {
              const newBadge =
                region.showNewBadge
                  ? '<span class="dsearch__badge dsearch__badge--new">Popular</span>'
                  : '';
              return `<li class="dsearch__item" data-item-id="${itemId}" data-item-title="${titleAttr}" data-url="${url}"><a class="${linkClass}" href="${url}"><span class="dsearch__result-content">${icon}<span class="dsearch__link-content"><span class="dsearch__result-name">${region.title}</span><span class="dsearch__price" data-price-target="${itemId}"><span class="dsearch__price-skeleton"></span></span></span>${newBadge}</span></a></li>`;
            }

            return `<li class="dsearch__item" data-item-id="${itemId}" data-item-title="${titleAttr}" data-url="${url}"><a class="${linkClass}" href="${url}"><span class="dsearch__result-content">${icon}<span class="dsearch__link-content"><span class="dsearch__result-name">${region.title}</span><span class="dsearch__price" data-price-target="${itemId}"><span class="dsearch__price-skeleton"></span></span></span></span><span class="dsearch__group-title">Region</span></a></li>`;
          })
          .join('');
        html += `<ul class="dsearch__list">${regionListHtml}</ul>`;
      }

      this.modalDropdown.innerHTML = html;
      this.modalDropdown.hidden = false;

      // Fetch prices in the background and update as they come in
      this.updatePricesInBackground(countries, regions);
    }

    async updatePricesInBackground(countries, regions) {
      // Use this.regions (all loaded regions) for lookup, but regions parameter for the items list
      const allRegionsForLookup = this.regions && this.regions.length > 0 ? this.regions : regions;

      // Fetch prices for all items, but update DOM as each completes
      const allItems = [
        ...countries.map((item, index) => ({ ...item, type: 'country', index })),
        ...regions.map((item, index) => ({ ...item, type: 'region', index })),
      ];

      // Process in batches to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < allItems.length; i += batchSize) {
        const batch = allItems.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (item) => {
            let price = null;
            const itemId = `${item.type}-${item.index}`;
            const priceElement = this.modalDropdown.querySelector(
              `[data-price-target="${itemId}"]`,
            );

            // For regions, check if they have productGid or collectionGid first
            if (item.type === 'region') {
              if (item.collectionGid) {
                try {
                  price = await this.fetchCollectionPrice(item.collectionGid);
                } catch (error) {
                  // Error fetching, will show blank below
                }
              } else if (item.productGid) {
                try {
                  price = await this.fetchProductPrice(item.productGid);
                } catch (error) {
                  // Error fetching, will show blank below
                }
              }
              // If still no price, try explicit productHandle (e.g. Global eSIM)
              if (!price && item.productHandle) {
                try {
                  price = await this.fetchProductPriceByHandle(item.productHandle);
                } catch (error) {
                  // Error fetching, will show blank below
                }
              }
              // If no GID, check URL for product or collection handle (skip for Global - use only productGid/productHandle)
              if (!price && item.url && !item.isGlobalProduct) {
                const productHandle = this.extractProductHandleFromUrl(item.url);
                const collectionHandle = this.extractCollectionHandleFromUrl(item.url);

                if (productHandle) {
                  try {
                    price = await this.fetchProductPriceByHandle(productHandle);
                  } catch (error) {
                    // Error fetching, will show blank below
                  }
                } else if (collectionHandle) {
                  try {
                    price = await this.fetchCollectionPriceByHandle(collectionHandle);
                  } catch (error) {
                    // Error fetching, will show blank below
                  }
                }
              }
            }

            // If no price from GID, try to extract handle from URL (skip for Global so it only uses productGid/productHandle)
            if (!price && !item.isGlobalProduct) {
              let collectionHandle = this.extractCollectionHandleFromUrl(item.url);
              let productHandle = this.extractProductHandleFromUrl(item.url);

              // If country URL doesn't have a collection/product handle (e.g., search URL),
              // try to find the region from region_reference and use its URL
              if (!collectionHandle && !productHandle && item.type === 'country') {
                let regionUrl = item.region_url && item.region_url.trim() ? item.region_url : null;

                // If region_url is not available or empty, try to get it from region_reference
                if (!regionUrl && item.region_reference && item.region_reference.trim()) {
                  // region_reference is a GID like gid://shopify/Metaobject/destination_region/{handle}
                  // Extract the handle (last part after the final slash)
                  const regionId = item.region_reference.split('/').pop();

                  // Find the matching region in loaded regions by handle
                  // Use allRegionsForLookup which is available in the closure
                  const foundRegion = allRegionsForLookup.find((region) => region.id === regionId);

                  if (foundRegion && foundRegion.url && foundRegion.url.trim()) {
                    regionUrl = foundRegion.url;
                  } else {
                    // If direct match fails, try case-insensitive match
                    const foundRegionAlt = allRegionsForLookup.find(
                      (region) => region.id.toLowerCase() === regionId.toLowerCase(),
                    );

                    if (foundRegionAlt && foundRegionAlt.url && foundRegionAlt.url.trim()) {
                      regionUrl = foundRegionAlt.url;
                    }
                  }
                }

                // Try to extract collection or product handle from region URL
                if (regionUrl && regionUrl.trim()) {
                  collectionHandle = this.extractCollectionHandleFromUrl(regionUrl);
                  if (!collectionHandle) {
                    productHandle = this.extractProductHandleFromUrl(regionUrl);
                  }
                }
              }

              // If we have a product handle, fetch price by handle
              if (productHandle) {
                try {
                  price = await this.fetchProductPriceByHandle(productHandle);
                } catch (error) {
                  // Error fetching, will show blank below
                }
              }
              // If we have a collection handle, fetch price by handle
              else if (collectionHandle) {
                try {
                  price = await this.fetchCollectionPriceByHandle(collectionHandle);
                } catch (error) {
                  // Error fetching, will show blank below
                }
              }
            }

            // Only update if this row still shows the same result (avoids wrong price on wrong row after re-render or index shift)
            const row = priceElement?.closest('.dsearch__item');
            const rowTitle = row?.getAttribute('data-item-title');
            if (rowTitle != null && rowTitle !== (item.title || '')) return;
            if (priceElement) {
              if (price) {
                priceElement.innerHTML = this.formatPrice(price);
              } else {
                // Remove the price element if no price is available
                priceElement.remove();
              }
            }
          }),
        );
      }
    }

    showLoading() {
      if (!this.modalDropdown) return;
      this.modalDropdown.innerHTML = '<div class="dsearch__group-title">Searching...</div>';
      this.modalDropdown.hidden = false;
    }

    showNoResults() {
      if (!this.modalDropdown) return;
      this.modalDropdown.innerHTML =
        '<div class="dsearch__group-title no-results">No results. Try searching another country or region.</div>';
      this.modalDropdown.hidden = false;
    }

    showError(message) {
      if (!this.modalDropdown) return;
      this.modalDropdown.innerHTML = `<div class="dsearch__group-title" style="color: #e74c3c;">${message}</div>`;
      this.modalDropdown.hidden = false;
    }

    hide() {
      if (this.modalDropdown) {
        this.modalDropdown.hidden = true;
        this.modalDropdown.innerHTML = '';
      }
    }

    async fetchAllCountries() {
      const allCountries = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const query = `query FetchCountries($first: Int!, $after: String) {
          metaobjects(type: "destination_country", first: $first, after: $after) {
            edges {
              node {
                handle
                fields {
                  key
                  value
                  reference {
                    ... on Metaobject {
                      id
                      handle
                    }
                  }
                  references(first: 20) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                        }
                      }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }`;

        const variables = { first: 50, after: cursor };

        const data = await this.executeGraphQLQuery(query, variables);

        if (!data) {
          throw new Error('Failed to fetch countries');
        }

        const countries = data?.metaobjects?.edges || [];
        allCountries.push(...countries);

        hasNextPage = data?.metaobjects?.pageInfo?.hasNextPage || false;
        cursor = data?.metaobjects?.pageInfo?.endCursor;
      }

      return allCountries;
    }

    async fetchAllRegions() {
      const allRegions = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const query = `query FetchRegions($first: Int!, $after: String) {
          metaobjects(type: "destination_region", first: $first, after: $after) {
            edges {
              node {
                handle
                fields { 
                  key 
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url(transform: {maxWidth: 32, maxHeight: 32, crop: CENTER})
                        altText
                        originalSrc
                      }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }`;

        const variables = { first: 50, after: cursor };

        const data = await this.executeGraphQLQuery(query, variables);

        if (!data) {
          throw new Error('Failed to fetch regions');
        }

        const regions = data?.metaobjects?.edges || [];
        allRegions.push(...regions);

        hasNextPage = data?.metaobjects?.pageInfo?.hasNextPage || false;
        cursor = data?.metaobjects?.pageInfo?.endCursor;
      }

      return allRegions;
    }

    async preloadData() {
      if (this.isDataLoaded && !this.needsRefresh()) {
        return Promise.resolve();
      }

      this.isLoading = true;
      this.showLoading();

      try {
        const [allCountries, allRegions] = await Promise.all([
          this.fetchAllCountries(),
          this.fetchAllRegions(),
        ]);

        this.countries = this.processCountries(allCountries);
        this.regions = this.processRegions(allRegions);
        this.isDataLoaded = true;
        this.lastFetch = Date.now();
      } catch (e) {
        this.showError('Failed to load search data');
      } finally {
        this.isLoading = false;
      }
    }

    async preloadDataSilently() {
      if (this.isDataLoaded && !this.needsRefresh()) {
        return Promise.resolve();
      }

      this.isLoading = true;

      try {
        const [allCountries, allRegions] = await Promise.all([
          this.fetchAllCountries(),
          this.fetchAllRegions(),
        ]);

        this.countries = this.processCountries(allCountries);
        this.regions = this.processRegions(allRegions);
        this.isDataLoaded = true;
        this.lastFetch = Date.now();
      } catch (e) {
        // Silent fail
      } finally {
        this.isLoading = false;
      }
    }

    needsRefresh() {
      return !this.isDataLoaded || Date.now() - this.lastFetch > this.cacheExpiry;
    }

    async searchLocal(q) {
      const searchTerm = (q || '').trim().toLowerCase();
      this.sendSearchEventLog(searchTerm || q);

      // Exact/substring matches (highest priority)
      const exactCountries = this.countries.filter((item) => {
        const matchesSearch = item.search.includes(searchTerm);
        const matchesTitle = item.title.toLowerCase().includes(searchTerm);
        return matchesSearch || matchesTitle;
      });

      const exactRegions = this.regions.filter((item) => {
        const matchesSearch = item.search.includes(searchTerm);
        const matchesTitle = item.title.toLowerCase().includes(searchTerm);
        return matchesSearch || matchesTitle;
      });

      // Fuzzy matching when query is non-empty (reuses levenshteinDistance from global.js)
      const FUZZY_PER_TOKEN_THRESHOLD = 2;
      const FUZZY_MAX_TOTAL_SCORE = 4;

      const getFuzzyScore = (queryTokens, item) => {
        const texts = [item.title, item.search].filter(Boolean).map((t) => (t || '').trim().toLowerCase());
        let bestScore = null;
        for (const searchableText of texts) {
          if (!searchableText) continue;
          const nameTokens = searchableText.split(/\s+/).filter(Boolean);
          if (nameTokens.length === 0) continue;
          const tokenDistances = queryTokens.map((queryToken) =>
            Math.min(...nameTokens.map((nameToken) => window.levenshteinDistance(queryToken, nameToken))),
          );
          if (tokenDistances.some((d) => d > FUZZY_PER_TOKEN_THRESHOLD)) continue;
          const score = tokenDistances.reduce((sum, d) => sum + d, 0);
          if (score > FUZZY_MAX_TOTAL_SCORE) continue;
          if (bestScore === null || score < bestScore) bestScore = score;
        }
        return bestScore;
      };

      let countryResults;
      let regionResults;

      if (searchTerm.length === 0) {
        countryResults = exactCountries;
        regionResults = exactRegions;
      } else {
        const queryTokens = searchTerm.split(/\s+/).filter(Boolean);
        const exactCountrySet = new Set(exactCountries);
        const exactRegionSet = new Set(exactRegions);

        const fuzzyCountries = this.countries
          .filter((c) => !exactCountrySet.has(c))
          .map((item) => {
            const score = getFuzzyScore(queryTokens, item);
            return score !== null ? { item, score } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.score - b.score)
          .map(({ item }) => item);

        const fuzzyRegions = this.regions
          .filter((r) => !exactRegionSet.has(r))
          .map((item) => {
            const score = getFuzzyScore(queryTokens, item);
            return score !== null ? { item, score } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.score - b.score)
          .map(({ item }) => item);

        countryResults = [...exactCountries, ...fuzzyCountries];
        regionResults = [...exactRegions, ...fuzzyRegions];
      }

      const countries = countryResults;

      // Find regions linked to matching countries
      const matchingRegions = [];
      const seenRegionTitles = new Set(regionResults.map((region) => region.title));

      countries.forEach((country) => {
        if (country.region_title && !seenRegionTitles.has(country.region_title)) {
          seenRegionTitles.add(country.region_title);
          matchingRegions.push({
            title: country.region_title,
            search: country.region_title.toLowerCase(),
            url: country.region_url || '',
            icon_url: country.region_icon_url || '',
          });
        } else if (country.region_reference) {
          const regionId = country.region_reference.split('/').pop();
          let foundRegion = this.regions.find((region) => region.id === regionId);

          if (!foundRegion) {
            const regionMappings = {
              australia: 'oceania',
              'new zealand': 'oceania',
              philippines: 'asia',
              japan: 'asia',
              china: 'asia',
              thailand: 'asia',
              singapore: 'asia',
              malaysia: 'asia',
              indonesia: 'asia',
              'south korea': 'asia',
              taiwan: 'asia',
              vietnam: 'asia',
              india: 'asia',
              france: 'europe',
              germany: 'europe',
              italy: 'europe',
              spain: 'europe',
              'united kingdom': 'europe',
              england: 'europe',
              netherlands: 'europe',
              switzerland: 'europe',
              austria: 'europe',
              belgium: 'europe',
              portugal: 'europe',
              greece: 'europe',
              norway: 'europe',
              sweden: 'europe',
              denmark: 'europe',
              finland: 'europe',
              poland: 'europe',
              'czech republic': 'europe',
              hungary: 'europe',
              croatia: 'europe',
              'united states': 'north america',
              canada: 'north america',
              mexico: 'north america',
              brazil: 'south america',
              argentina: 'south america',
              chile: 'south america',
              'south africa': 'africa',
              egypt: 'africa',
              morocco: 'africa',
              kenya: 'africa',
              tanzania: 'africa',
            };

            const countryName = country.title.toLowerCase();
            const expectedRegion = regionMappings[countryName];

            if (expectedRegion) {
              foundRegion = this.regions.find((region) =>
                region.title.toLowerCase().includes(expectedRegion),
              );
            }
          }

          if (foundRegion && !seenRegionTitles.has(foundRegion.title)) {
            seenRegionTitles.add(foundRegion.title);
            matchingRegions.push(foundRegion);
          }
        }

        if (country.additional_region_handles && country.additional_region_handles.length > 0) {
          country.additional_region_handles.forEach((handle) => {
            const extraRegion = this.regions.find(
              (r) => r.id === handle || r.id.toLowerCase() === String(handle).toLowerCase(),
            );
            if (extraRegion && !seenRegionTitles.has(extraRegion.title)) {
              seenRegionTitles.add(extraRegion.title);
              matchingRegions.push(extraRegion);
            }
          });
        }
      });

      regionResults = [...regionResults, ...matchingRegions];

      // If any matched country is covered by the Global eSIM, add Global to region results.
      // Use the search term (what the user typed) and fuzzy-match it against countries_covered,
      // so e.g. "Indnesia" matches "Indonesia" and we show Global.
      const GLOBAL_FUZZY_THRESHOLD = 3;
      if (
        this.globalEsimCountries &&
        this.globalEsimCountries.size > 0 &&
        this.globalEsimUrl &&
        countryResults.length > 0 &&
        searchTerm.length > 0
      ) {
        const hasGlobalInResults = regionResults.some(
          (r) => r.title && r.title.trim().toLowerCase() === 'global',
        );
        const coveredList = [...this.globalEsimCountries];
        const isCountryCoveredByGlobal =
          this.globalEsimCountries.has(searchTerm) ||
          (typeof window.levenshteinDistance === 'function' &&
            coveredList.some(
              (covered) => window.levenshteinDistance(searchTerm, covered) <= GLOBAL_FUZZY_THRESHOLD,
            ));
        if (isCountryCoveredByGlobal && !hasGlobalInResults) {
          regionResults = [
            ...regionResults,
            {
              title: this.globalEsimTitle,
              search: this.globalEsimTitle.toLowerCase(),
              url: this.globalEsimUrl,
              icon_url: this.globalEsimIconUrl || '',
              productGid: this.globalEsimProductGid || null,
              productHandle: this.globalEsimHandle || null,
              isGlobalProduct: true,
              showNewBadge: this.globalEsimNew,
            },
          ];
        }
      }

      if (countryResults.length > 0 || regionResults.length > 0) {
        await this.render(countryResults, regionResults);
      } else {
        this.showNoResults();
      }
    }

    sendSearchEventLog(searchTerm, clicked = false) {
      clearTimeout(this.debounceTimer);

      const time = clicked ? 0 : 1000;

      this.debounceTimer = setTimeout(() => {
        const endpoint = 'https://portal.simify.com/api/v2/webhooks/customEvent';

        const country = Shopify.country;
        const payload = {
          eventSource: 'search_input',
          eventType: 'search_term',
          additionalInfo: (clicked ? `${searchTerm};clicked` : searchTerm) + `;${country}`,
        };

        const data = JSON.stringify(payload);

        try {
          if (navigator.sendBeacon) {
            navigator.sendBeacon(endpoint, data);
          } else {
            fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: data,
              keepalive: true,
            });
          }
        } catch (err) {
          console.error('Error sending beacon:', err);
        }
      }, time);
    }

    processCountries(countries) {
      const toMap = (fields) => {
        const obj = {};
        (fields || []).forEach((f) => (obj[f.key] = f.value));
        return obj;
      };

      return countries.map((e) => {
        const fields = e.node.fields || [];
        const m = toMap(fields);
        const regionField = fields.find((f) => f.key === 'region');
        const regionReference =
          regionField?.reference?.handle != null
            ? regionField.reference.handle
            : (regionField?.reference?.id && typeof regionField.reference.id === 'string'
                ? regionField.reference.id.split('/').pop()
                : null) || m.region || '';

        const additionalRegionHandles = (fields || [])
          .filter((f) => f.key === 'additional_regions')
          .flatMap((f) => {
            const fromList = f.references?.edges
              ?.map((edge) => edge?.node?.handle ?? (edge?.node?.id ? String(edge.node.id).split('/').pop() : null))
              .filter(Boolean);
            if (fromList && fromList.length > 0) return fromList;
            if (f.reference?.handle != null) return [f.reference.handle];
            if (f.reference?.id && typeof f.reference.id === 'string')
              return [f.reference.id.split('/').pop()];
            return [];
          })
          .filter(Boolean);

        return {
          id: e.node.handle,
          title: m.title || '',
          search: (m.search_title || m.title || '').toLowerCase(),
          url: m.url || '',
          icon_url: m.icon_url || '',
          region_title: m.region_title || '',
          region_url: m.region_url || '',
          region_icon_url: m.region_icon_url || '',
          region_reference: regionReference,
          additional_region_handles: additionalRegionHandles,
        };
      });
    }

    processRegions(regions) {
      const toMap = (fields) => {
        const obj = {};
        (fields || []).forEach((f) => {
          obj[f.key] = f.value;

          if (
            f.key === 'icon' &&
            f.value &&
            typeof f.value === 'string' &&
            f.value.startsWith('gid://shopify/MediaImage/')
          ) {
            obj[`${f.key}_gid`] = f.value;
          }

          if (f.reference) {
            if (f.reference.__typename === 'MediaImage' && f.reference.image) {
              if (f.reference.image.url) {
                obj[`${f.key}_url`] = f.reference.image.url;
              } else if (f.reference.image.originalSrc) {
                const storeDomain = window.location.hostname;
                const imageUrl = `${f.reference.image.originalSrc}?v=1234567890&width=32&height=32&crop=center`;
                obj[`${f.key}_url`] = imageUrl;
              }
            } else if (f.reference.url) {
              obj[`${f.key}_url`] = f.reference.url;
            } else {
              const gid = f.reference.id || f.reference;
              if (gid && typeof gid === 'string' && gid.startsWith('gid://shopify/MediaImage/')) {
                const mediaId = gid.split('/').pop();
                const imageUrl = `//simify.com/cdn/shop/files/${mediaId}.png?v=1234567890&width=32&height=32&crop=center`;
                obj[`${f.key}_url`] = imageUrl;
              }
            }
          }
        });
        return obj;
      };

      return regions.map((e) => {
        const m = toMap(e.node.fields);
        let collectionGid = null;
        let productGid = null;

        // Extract collection and product GIDs from fields (similar to popular destinations)
        e.node.fields.forEach((field) => {
          // Check for collection GID in value
          if (
            field.key === 'collection_page' &&
            field.value &&
            field.value.startsWith('gid://shopify/Collection/')
          ) {
            collectionGid = field.value;
          }

          // Check for product GID in value
          if (
            field.key === 'product' &&
            field.value &&
            field.value.startsWith('gid://shopify/Product/')
          ) {
            productGid = field.value;
          }
        });

        return {
          id: e.node.handle,
          title: m.link_title || m.title || '',
          search: (m.link_title || m.title || '').toLowerCase(),
          url: m.link_url || m.url || '',
          icon_url: m.icon_url || '',
          collectionGid: collectionGid,
          productGid: productGid,
        };
      });
    }

    async showPopularDestinations() {
      if (!this.storefrontToken) {
        return;
      }

      try {
        const popularDestinations = await this.fetchPopularDestinations();
        await this.renderPopularDestinations(popularDestinations);
      } catch (error) {
        console.error('Error in showPopularDestinations:', error);
      }
    }

    async fetchPopularDestinations() {
      const query = `query FetchPopularDestinations {
        metaobjects(type: "popular_destination_tile", first: 20) {
          edges {
            node {
              handle
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url(transform: {maxWidth: 32, maxHeight: 32, crop: CENTER})
                      altText
                    }
                  }
                  ... on Collection {
                    id
                    handle
                    title
                    products(first: 10) {
                      edges {
                        node {
                          id
                          title
                          priceRange {
                            minVariantPrice {
                              amount
                              currencyCode
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`;

      const data = await this.executeGraphQLQuery(query);

      const filteredData = data.metaobjects.edges
        .filter((e) => e.node.fields.some((f) => f.key === 'position'))
        .sort((a, b) => {
          const getPosition = (node) => {
            const positionField = node.fields.find((f) => f.key === 'position');
            return positionField ? Number(positionField.value) : 0;
          };

          return getPosition(a.node) - getPosition(b.node);
        });

      return filteredData;
    }

    async fetchCollectionPrice(collectionGid) {
      const contextDir = this.getContextCountry() ? ` @inContext(country: ${this.getContextCountry()})` : '';
      const query = `query FetchCollectionPrice($id: ID!)${contextDir} {
        collection(id: $id) {
          id
          products(first: 10) {
            edges {
              node {
                productType
                metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE_CUSTOM}", key: "${CONSTANTS.METAFIELD_KEY_CARD_SPECIFICATION}") {
                  value
                }
                from_price_metafield: metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_FROM_PRICE}") {
                  value
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_DAYS}") {
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`;

      try {
        const data = await this.executeGraphQLQuery(query, { id: collectionGid });
        const collection = data?.collection;
        if (!collection || !collection.products?.edges?.length) {
          return null;
        }

        let minPriceResult = null;
        let minCandidateInCart = null;
        let hasUnlimited = false;

        const considerProduct = (product) => {
          let candidateInCart = null;
          let candidatePrice = null;

          const fromPrice = this.getPriceFromFromPriceMetafield(product);
          if (fromPrice) {
            candidateInCart = this.convertToDisplayCurrency(fromPrice.amount, fromPrice.currencyCode);
            candidatePrice = fromPrice;
          } else if (product.variants) {
            const variant = this.findMinPriceVariant(product.variants);
            if (variant) {
              candidateInCart = this.getPerDayAmountInCartCurrency(variant);
              candidatePrice = this.getPriceForDisplay(variant);
            }
          }

          if (candidateInCart != null && candidatePrice != null) {
            if (minCandidateInCart == null || candidateInCart < minCandidateInCart) {
              minCandidateInCart = candidateInCart;
              minPriceResult = candidatePrice;
            }
          }
        };

        collection.products.edges.forEach((edge) => {
          const product = edge.node;
          const cardSpec = product.metafield?.value || '';
          if (cardSpec.includes(CONSTANTS.UNLIMITED_VALUE)) hasUnlimited = true;
        });

        if (hasUnlimited) {
          collection.products.edges.forEach((edge) => {
            const product = edge.node;
            const cardSpec = product.metafield?.value || '';
            if (cardSpec.includes(CONSTANTS.UNLIMITED_VALUE)) considerProduct(product);
          });
        }
        if (!hasUnlimited || minPriceResult == null) {
          collection.products.edges.forEach((edge) => {
            const product = edge.node;
            const cardSpec = product.metafield?.value || '';
            if (!cardSpec.includes(CONSTANTS.UNLIMITED_VALUE)) considerProduct(product);
          });
        }

        return minPriceResult;
      } catch (error) {
        console.error('Error fetching collection price:', error);
        return null;
      }
    }

    async fetchProductPriceByHandle(productHandle) {
      const contextDir = this.getContextCountry() ? ` @inContext(country: ${this.getContextCountry()})` : '';
      const query = `query FetchProductByHandle($handle: String!)${contextDir} {
        product(handle: $handle) {
          id
          productType
          metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE_CUSTOM}", key: "${CONSTANTS.METAFIELD_KEY_CARD_SPECIFICATION}") {
            value
          }
          from_price_metafield: metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_FROM_PRICE}") {
            value
          }
          variants(first: 10) {
            edges {
              node {
                id
                price {
                  amount
                  currencyCode
                }
                metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_DAYS}") {
                  value
                }
              }
            }
          }
        }
      }`;

      try {
        const data = await this.executeGraphQLQuery(query, { handle: productHandle });
        const product = data?.product;
        if (!product || !product.variants) {
          return null;
        }

        const fromPrice = this.getPriceFromFromPriceMetafield(product);
        if (fromPrice) return fromPrice;

        const minPriceVariant = this.findMinPriceVariant(product.variants);
        return minPriceVariant ? this.getPriceForDisplay(minPriceVariant) : null;
      } catch (error) {
        console.error('Error fetching product price by handle:', error);
        return null;
      }
    }

    async fetchProductPrice(productGid) {
      const contextDir = this.getContextCountry() ? ` @inContext(country: ${this.getContextCountry()})` : '';
      const query = `query FetchProductPrice($id: ID!)${contextDir} {
        product(id: $id) {
          id
          productType
          metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE_CUSTOM}", key: "${CONSTANTS.METAFIELD_KEY_CARD_SPECIFICATION}") {
            value
          }
          from_price_metafield: metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_FROM_PRICE}") {
            value
          }
          variants(first: 10) {
            edges {
              node {
                id
                price {
                  amount
                  currencyCode
                }
                metafield(namespace: "${CONSTANTS.METAFIELD_NAMESPACE}", key: "${CONSTANTS.METAFIELD_KEY_DAYS}") {
                  value
                }
              }
            }
          }
        }
      }`;

      try {
        const data = await this.executeGraphQLQuery(query, { id: productGid });
        const product = data?.product;
        if (!product || !product.variants) {
          return null;
        }

        const fromPrice = this.getPriceFromFromPriceMetafield(product);
        if (fromPrice) return fromPrice;

        const minPriceVariant = this.findMinPriceVariant(product.variants);
        return minPriceVariant ? this.getPriceForDisplay(minPriceVariant) : null;
      } catch (error) {
        console.error('Error fetching product price:', error);
        return null;
      }
    }

    async renderPopularDestinations(popularDestinations) {
      if (!this.modalDropdown) return;

      if (!popularDestinations.length) {
        this.modalDropdown.innerHTML =
          '<div class="popular-destinations-title">No popular destinations available</div>';
        this.modalDropdown.hidden = false;
        return;
      }

      let html =
        '<div class="dsearch__group-wrapper"><div class="popular-destinations-title">Popular Destinations</div><ul class="dsearch__list">';

      // Process destinations and prepare data (without waiting for prices)
      const destinationsData = popularDestinations.map((destination) => {
        const fields = {};
        let collectionGid = null;
        let productGid = null;

        destination.node.fields.forEach((field) => {
          fields[field.key] = field.value;

          if (
            field.reference &&
            field.reference.__typename === 'MediaImage' &&
            field.reference.image
          ) {
            fields[`${field.key}_url`] = field.reference.image.url;
          }

          // Check for collection GID in value (since reference might be null)
          if (
            field.key === 'collection_page' &&
            field.value &&
            field.value.startsWith('gid://shopify/Collection/')
          ) {
            collectionGid = field.value;
          }

          // Check for product GID in value
          if (
            field.key === 'product' &&
            field.value &&
            field.value.startsWith('gid://shopify/Product/')
          ) {
            productGid = field.value;
          }
        });

        return { fields, collectionGid, productGid };
      });

      // Render immediately without prices
      destinationsData.forEach((destination, index) => {
        const { fields, collectionGid, productGid } = destination;

        // Add data attribute for price fetching
        const itemId = `popular-${index}`;

        const title = fields.title || '';
        const imageUrl = fields.image_url || '';
        const iconUrl = fields.icon_url || '';
        const url = this.resolveLocalizedUrl(fields.link || '#');
        const newProduct =
          fields.New === true ||
          fields.New === 'true' ||
          fields.new === true ||
          fields.new === 'true';
        const destinationType = fields.type || fields.Type || ''; // Check for Type field (Country/Region)

        const finalIconUrl = iconUrl || imageUrl;
        const icon = finalIconUrl
          ? `<img src="${finalIconUrl}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
          : '';

        // New badge goes inside dsearch__result-content
        const newBadge = newProduct
          ? '<span class="dsearch__badge dsearch__badge--new">Popular</span>'
          : '';

        // Type badge (Country/Region) goes outside dsearch__result-content
        let typeBadge = '';
        if (destinationType) {
          // Capitalize first letter, lowercase the rest
          const typeLabel =
            destinationType.charAt(0).toUpperCase() + destinationType.slice(1).toLowerCase();
          typeBadge = `<span class="dsearch__group-title">${typeLabel}</span>`;
        }

        // Store GIDs for background price fetching
        const gidData =
          collectionGid || productGid
            ? `data-collection-gid="${collectionGid || ''}" data-product-gid="${productGid || ''}"`
            : '';

        html += `<li class="dsearch__item" data-item-id="${itemId}" ${gidData}><a class="dsearch__link" href="${url}"><span class="dsearch__result-content">${icon}<span class="dsearch__link-content"><span class="dsearch__link-title">${title}</span><span class="dsearch__price" data-price-target="${itemId}"><span class="dsearch__price-skeleton"></span></span></span>${newBadge}</span>${typeBadge}</a></li>`;
      });

      html += '</ul></div>';
      this.modalDropdown.innerHTML = html;
      this.modalDropdown.hidden = false;

      // Fetch prices in the background and update as they come in
      this.updatePopularDestinationsPrices(destinationsData);
    }

    async updatePopularDestinationsPrices(destinationsData) {
      // Process in batches to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < destinationsData.length; i += batchSize) {
        const batch = destinationsData.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (destination, batchIndex) => {
            const index = i + batchIndex;
            const { collectionGid, productGid } = destination;
            const itemId = `popular-${index}`;
            const priceElement = this.modalDropdown.querySelector(
              `[data-price-target="${itemId}"]`,
            );

            if (!priceElement) return;

            try {
              let price = null;
              if (collectionGid) {
                price = await this.fetchCollectionPrice(collectionGid);
              } else if (productGid) {
                price = await this.fetchProductPrice(productGid);
              }

              if (price) {
                priceElement.innerHTML = this.formatPrice(price);
              } else {
                // Remove the price element if no price is available
                priceElement.remove();
              }
            } catch (error) {
              // On error, remove the price element
              priceElement.remove();
            }
          }),
        );
      }
    }
  }

  customElements.define('destinations-search-modal', DestinationsSearchModal);
})();
